import type { FC, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import KibTag from '@chewy/kib-tag-react'
import classNames from 'classnames'
import {
  type Constant,
  type Patient,
  PatientAvatar,
  Utils,
} from '@pbt/pbt-ui-components'

import ClientInfo from '~/components/common/ClientInfo'
import { getShortGenderString } from '~/components/common/inputs/gender/genderUtils'
import MetadataList from '~/components/common/MetadataList'
import { PatientPreferencesChip } from '~/components/dashboard/clients/patient/PatientPreferencesChip'
import { useAlertType, usePatientWeight } from '~/store/hooks/patient'
import { getBreed, getGender, getSpecies } from '~/store/reducers/constants'
import { getAppointmentId } from '~/store/reducers/soap'
import { getTimetableEvent } from '~/store/reducers/timetable'
import { getUser } from '~/store/reducers/users'
import { getFullBreedName, getPatientAge } from '~/utils'

import RhapWellnessPlanMemberButton from './RhapWellnessPlanMemberButton'

import '@chewy/kib-tag-styles/dist/kib-tag.css'
import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import styles from './PatientDetailsHeader.module.scss'

export interface PatientDetailsHeaderProps {
  actionsSlot?: ReactNode
  clientId?: string
  patient?: Patient
}

const PatientDetailsHeader: FC<PatientDetailsHeaderProps> = ({
  clientId,
  patient,
  actionsSlot,
}) => {
  const { id, name, species, breeds, gender, color, photo, spayedNeutered } =
    patient || {}
  const client = useSelector(getUser(clientId))
  const speciesConstants = useSelector(getSpecies)
  const appointmentId = useSelector(getAppointmentId)
  const appointment = useSelector(getTimetableEvent(appointmentId))

  const age = getPatientAge(patient, true)
  const Gender: Constant[] = useSelector(getGender)
  const patientWeight = usePatientWeight(id, undefined, false)
  const breedMap = useSelector(getBreed)
  const genderString = getShortGenderString(gender, spayedNeutered, Gender)
  const breedString = getFullBreedName(species, breeds, breedMap)

  const { alertsToRender } = useAlertType(id)

  return (
    <div className={styles.container}>
      <PatientAvatar
        animal={Utils.getConstantName(species, speciesConstants)}
        classes={{ root: styles.profileAvatar }}
        src={photo || undefined}
      />
      <div
        className={classNames(styles.dataRows, {
          [styles.maxWidth]: Boolean(actionsSlot),
        })}
      >
        <div className={styles.responsiveContainer}>
          <div className={styles.line1}>
            <div className={styles.patientIdentity}>
              <PatientPreferencesChip
                isDisplayWidgetChirp
                appointment={appointment}
                className={styles.preferencesChip}
                patientId={patient?.id}
              />
              <div className={styles.patientNameWrapper}>
                <a
                  className={classNames(styles.patientName, styles.plainLink)}
                  href={`/client/${clientId}/patient/${patient?.id}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {`${name || ''} ${client?.lastName || ''}`.trim()}
                </a>
              </div>
            </div>
            <div className={styles.patientAttributes}>
              <MetadataList
                isTruncated={Boolean(actionsSlot)}
                items={[
                  { value: age },
                  { value: breedString },
                  { value: genderString },
                  { value: color || '' },
                  { value: patientWeight || '' },
                ]}
              />
            </div>
          </div>
          <div className={styles.line2}>
            <div className={styles.wellnessAndAlerts}>
              <RhapWellnessPlanMemberButton
                className={styles.wellnessPlanButton}
                clientId={clientId}
                patientId={patient?.id}
              />
              {alertsToRender.map((alert) => (
                <KibTag key={alert} theme="error">
                  {alert}
                </KibTag>
              ))}
            </div>
            <ClientInfo
              appointment={appointment}
              client={client}
              hasActionsSlot={Boolean(actionsSlot)}
            />
          </div>
        </div>
      </div>
      {actionsSlot && (
        <div className={styles.menuButtonContainer}>{actionsSlot}</div>
      )}
    </div>
  )
}

export default PatientDetailsHeader
